import { doc, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { computeStandings, generatePlayoffFromGroups } from '@/engine';
import { matchesCol } from './matches';
import { participantsCol } from './participants';
import type { Match, Participant, StandingRow, Tournament } from './types';

export async function generatePlayoff(
  tournament: Tournament
): Promise<{ sameGroupR1Conflicts: number }> {
  if (tournament.format !== 'GROUPS_PLAYOFF') throw new Error('Не GP');
  if (!tournament.groups) throw new Error('Группы не сформированы');
  if (!tournament.groupConfig) throw new Error('groupConfig не задан');

  const matchesSnap = await getDocs(matchesCol(tournament.id));
  const allMatches: Match[] = matchesSnap.docs.map((d) => d.data());
  const groupMatches = allMatches.filter((m) => m.stage === 'GROUP');
  const incomplete = groupMatches.filter((m) => m.status !== 'COMPLETED');
  if (incomplete.length > 0) {
    throw new Error(`Сначала доиграйте групповой этап (осталось ${incomplete.length})`);
  }
  if (allMatches.some((m) => m.stage === 'PLAYOFF')) {
    throw new Error('Плей-офф уже сгенерирован');
  }

  const participantsSnap = await getDocs(participantsCol(tournament.id));
  const participants: Participant[] = participantsSnap.docs.map((d) => d.data());
  const participantById = new Map(participants.map((p) => [p.id, p]));

  // Вычислить standings отдельно по каждой группе
  const standingsAll: StandingRow[] = [];
  for (const g of tournament.groups) {
    const groupMs = groupMatches.filter((m) => m.groupId === g.id);
    const rows = computeStandings(groupMs, g.participantIds);
    for (const r of rows) standingsAll.push({ ...r, groupId: g.id });
  }

  const playoff = generatePlayoffFromGroups(
    tournament.groups,
    standingsAll,
    tournament.groupConfig.advancePerGroup
  );

  // Лёгкая защита от гонки двойной генерации: повторно читаем matches
  // непосредственно перед commit. Полная транзакция избыточна для MVP —
  // это закрывает почти все реалистичные гонки.
  const recheckSnap = await getDocs(matchesCol(tournament.id));
  if (recheckSnap.docs.some((d) => d.data().stage === 'PLAYOFF')) {
    throw new Error('Плей-офф уже сгенерирован');
  }

  const batch = writeBatch(db);
  for (const g of playoff.matches) {
    const ref = doc(db, 'tournaments', tournament.id, 'matches', g.id);
    const aName = g.playerAId ? participantById.get(g.playerAId)?.displayName ?? null : null;
    const bName = g.playerBId ? participantById.get(g.playerBId)?.displayName ?? null : null;
    const m: Match = {
      id: g.id,
      stage: 'PLAYOFF',
      round: g.round,
      slot: g.slot,
      playerAId: g.playerAId,
      playerBId: g.playerBId,
      playerAName: aName,
      playerBName: bName,
      status: g.playerAId && g.playerBId ? 'READY' : 'PENDING',
      ...(g.nextMatchId ? { nextMatchId: g.nextMatchId } : {}),
      ...(g.nextMatchSlot ? { nextMatchSlot: g.nextMatchSlot } : {}),
    };
    batch.set(ref, m);
  }
  batch.update(doc(db, 'tournaments', tournament.id), {
    updatedAt: serverTimestamp(),
  });
  await batch.commit();

  return { sameGroupR1Conflicts: playoff.sameGroupR1Conflicts ?? 0 };
}
